import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { inviteToCaseModal } from 'src/components/pages/Case/Case.slice';
import { CasePermissionsType, CaseTypeEnum } from 'src/types';
import { Access } from '../../InviteToCaseModal/components/Access/Access';
import { AccessActionEnum } from '../../InviteToCaseModal/components/AccessItem/AccessItem';
import { accessStepPermissionHelper } from '../../InviteToCaseModal/helpers/accessStepPermissionHelper';
import { InviteUserDropdownOption } from '../InviteToCaseTeamDrawer';
import { UserChips } from '../components/UserChips';

interface AccessStepProps {
  caseType?: CaseTypeEnum;
  permissions: CasePermissionsType[];
  handleSetPermissions: (
    permissions: CasePermissionsType[],
    action: AccessActionEnum,
  ) => void;
  selectedSuperAdmins: InviteUserDropdownOption[];
  selectedApprovers: InviteUserDropdownOption[];
  handleDeleteUserChip: (user: InviteUserDropdownOption) => void;
  isReadOnly?: boolean;
}

const PermissionsCard = ({
  title,
  description,
  onCardClick,
  dataTestId,
  isSelected,
  tokens,
  sp,
  disabled = false,
}: {
  title: string;
  description: string;
  onCardClick: () => void;
  dataTestId: string;
  isSelected?: boolean;
  tokens: LensTheme['tokens'];
  sp: LensTheme['tokens']['semantic']['spacing']['desktop']['gap'];
  disabled?: boolean;
}) => {
  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: sp[1].value,
        cursor: disabled ? 'not-allowed' : 'pointer',
        border:
          isSelected && !disabled
            ? `2px ${tokens.semantic.color.outline.active.value} solid`
            : isSelected && disabled
              ? `2px ${tokens.semantic.color.action.primary.disabled.value} solid`
              : `1px ${tokens.semantic.color.outline.disabled.value} solid`,
        '&:hover': {
          background: !disabled
            ? tokens.semantic.color.surface.subtle.value
            : undefined,
        },
      }}
      onClick={disabled ? undefined : onCardClick}
      data-testid={dataTestId}
    >
      <CardHeader
        title={
          <Typography variant="h5" component="h2" fontWeight="600">
            {title}
          </Typography>
        }
      />
      <CardContent>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

export const AccessStep = ({
  caseType,
  permissions,
  handleSetPermissions,
  selectedSuperAdmins,
  selectedApprovers,
  handleDeleteUserChip,
  isReadOnly = false,
}: AccessStepProps) => {
  const { tokens } = useTheme<LensTheme>();
  const sp = tokens.semantic.spacing.desktop.gap;

  const { unableToChat } = useSelector(inviteToCaseModal);

  const {
    hasAllViewPermissions,
    hasAllEditPermissions,
    disabledViewReportPII,
    disabledViewCasePII,
    ALL_VIEW_PERMISSIONS,
    ALL_EDIT_PERMISSIONS,
  } = accessStepPermissionHelper(permissions, unableToChat);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: sp[3].value }}>
      {!isReadOnly && (
        <UserChips
          selectedSuperAdmins={selectedSuperAdmins}
          selectedApprovers={selectedApprovers}
          handleDeleteUserChip={handleDeleteUserChip}
        />
      )}
      <Divider />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: sp[1].value }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
          Access level
        </Typography>
        <PermissionsCard
          title="Editor access"
          description="Grant full control and unrestricted editing for the entire case, allowing modification of all relevant information."
          onCardClick={() => {
            handleSetPermissions(
              ALL_VIEW_PERMISSIONS.filter((p) => permissions.includes(p)),
              AccessActionEnum.REMOVE,
            );
            handleSetPermissions(ALL_EDIT_PERMISSIONS, AccessActionEnum.ADD);
          }}
          dataTestId="all-edit-access"
          isSelected={hasAllEditPermissions}
          tokens={tokens}
          sp={sp}
          disabled={isReadOnly}
        />
        <PermissionsCard
          title="Read-only access"
          description="Grant read-only access for the entire case, allowing viewing of all relevant information."
          onCardClick={() => {
            handleSetPermissions(
              ALL_EDIT_PERMISSIONS.filter((p) => permissions.includes(p)),
              AccessActionEnum.REMOVE,
            );
            handleSetPermissions(ALL_VIEW_PERMISSIONS, AccessActionEnum.ADD);
          }}
          dataTestId="all-read-access"
          isSelected={hasAllViewPermissions && !hasAllEditPermissions}
          tokens={tokens}
          sp={sp}
          disabled={isReadOnly}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
          Refine access
        </Typography>
        <Box sx={{ display: 'flex', gap: sp[3].value }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            Read-only
          </Typography>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            Editor
          </Typography>
        </Box>
      </Box>
      <Access
        newEnv={true}
        caseType={caseType}
        permissions={permissions}
        handleSetPermissions={handleSetPermissions}
        disabledViewCasePII={disabledViewCasePII}
        disabledViewReportPII={disabledViewReportPII}
        unableToChat={unableToChat}
        disablePermissions={isReadOnly}
        isSuperAdmin={
          selectedSuperAdmins.length > 0 && selectedApprovers.length === 0
        }
      />
    </Box>
  );
};
